import React, { useState } from 'react';
import {
    Box, Card, CardContent, Typography, Grid, TextField, FormControl,
    InputLabel, Select, MenuItem, Chip, Button, Paper,
    FormControlLabel, Checkbox, Alert, Stepper, Step, StepLabel,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    LinearProgress, Accordion, AccordionSummary, AccordionDetails, useTheme, alpha
} from '@mui/material';
import {
    Add as AddIcon,
    DeleteOutline as TrashIcon,
    CheckCircle as CheckIcon,
    Assignment as AssignmentIcon,
    Timeline as TimelineIcon,
    Assessment as AssessmentIcon,
    Engineering as EngineeringIcon,
    School as TrainingIcon,
    Gavel as ComplianceIcon,
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

export default function D6Implementation({ step, onStepUpdate, stepMapping, stepId, isReadOnly, batchTimeFormat = '12hour' }) {
    const theme = useTheme();
    const [activePhase, setActivePhase] = useState(0);

    const updateStep = (updates) => {
        onStepUpdate(stepMapping[stepId], { ...step, ...updates });
    };

    const implementationPhases = [
        { name: 'Strategy & Planning', icon: <AssignmentIcon />, color: '#2196f3' },
        { name: 'Pilot & Validation', icon: <AssessmentIcon />, color: '#ff9800' },
        { name: 'Full Deployment', icon: <EngineeringIcon />, color: '#4caf50' },
        { name: 'Training & Change Mgmt', icon: <TrainingIcon />, color: '#9c27b0' },
        { name: 'Monitoring & Control', icon: <TimelineIcon />, color: '#f44336' },
        { name: 'Compliance & Sustain', icon: <ComplianceIcon />, color: '#607d8b' }
    ];

    const addImplementationAction = () => {
        const newActions = [...(step.implementationActions || []), {
            id: Date.now(),
            title: '',
            description: '',
            phase: 'strategy',
            owner: '',
            department: '',
            startDate: '',
            targetDate: '',
            actualDate: '',
            status: 'planned',
            progress: 0,
            risks: '',
            dependencies: ''
        }];
        updateStep({ implementationActions: newActions });
    };

    const updateImplementationAction = (index, field, value) => {
        const updatedActions = [...(step.implementationActions || [])];
        updatedActions[index] = { ...updatedActions[index], [field]: value };
        updateStep({ implementationActions: updatedActions });
    };

    const deleteImplementationAction = (index) => {
        const updatedActions = (step.implementationActions || []).filter((_, i) => i !== index);
        updateStep({ implementationActions: updatedActions });
    };

    const addTrainingProgram = () => {
        const newPrograms = [...(step.trainingPrograms || []), {
            id: Date.now(),
            programName: '',
            targetAudience: '',
            trainingMethod: '',
            duration: '',
            competencyRequirements: '',
            assessmentMethod: '',
            completionCriteria: '',
            status: 'planned'
        }];
        updateStep({ trainingPrograms: newPrograms });
    };

    const updateTrainingProgram = (index, field, value) => {
        const updatedPrograms = [...(step.trainingPrograms || [])];
        updatedPrograms[index] = { ...updatedPrograms[index], [field]: value };
        updateStep({ trainingPrograms: updatedPrograms });
    };

    const isStrategyPlanningComplete = () => {
        return step.implementationStrategy?.trim() && 
               step.rolloutPlan?.trim() && 
               step.resourceAllocation?.trim() &&
               step.stakeholderPlan?.trim() &&
               step.successMetrics?.trim();
    };

    const isPilotValidationComplete = () => {
        return step.pilotScope?.trim() && 
               step.validationCriteria?.trim() && 
               step.pilotResults?.trim() &&
               step.pilotStartDate &&
               step.pilotEndDate &&
               step.pilotStatus;
    };

    const isFullDeploymentComplete = () => {
        return step.deploymentPlan?.trim() && 
               step.rolloutSchedule?.trim() && 
               step.deploymentStatus &&
               step.deploymentProgress !== undefined && step.deploymentProgress !== null && step.deploymentProgress !== '';
    };

    const isTrainingChangeManagementComplete = () => {
        const programs = step.trainingPrograms || [];
        const programsComplete = programs.length > 0 && programs.every(program => 
            program.programName?.trim() &&
            program.targetAudience?.trim() &&
            program.trainingMethod &&
            program.duration?.trim() &&
            program.competencyRequirements?.trim() &&
            program.assessmentMethod?.trim() &&
            program.completionCriteria?.trim() &&
            program.status
        );
        return programsComplete && 
               step.changeManagementPlan?.trim() && 
               step.communicationPlan?.trim();
    };

    const isMonitoringControlComplete = () => {
        return step.monitoringPlan?.trim() && 
               step.kpiDefinitions?.trim() && 
               step.controlMechanisms?.trim() &&
               step.reportingPlan?.trim();
    };

    const isComplianceSustainabilityComplete = () => {
        return step.complianceValidation?.trim() && 
               step.sustainabilityPlan?.trim() && 
               step.documentationStatus &&
               step.effectivenessRating !== undefined && step.effectivenessRating !== null && step.effectivenessRating !== '' &&
               step.implementationSummary?.trim();
    };

    const getOverallProgress = () => {
        const actions = step.implementationActions || [];
        if (actions.length === 0) return 0;
        const totalProgress = actions.reduce((sum, action) => sum + (action.progress || 0), 0);
        return Math.round(totalProgress / actions.length);
    };

    // Overall form validation function
    const isFormValid = () => {
        return isStrategyPlanningComplete() && 
               isPilotValidationComplete() && 
               isFullDeploymentComplete() && 
               isTrainingChangeManagementComplete() && 
               isMonitoringControlComplete() && 
               isComplianceSustainabilityComplete();
    };

    const renderStrategyPlanning = () => (
        <Card sx={{ 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <AssignmentIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                            Implementation Strategy & Planning
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Define comprehensive implementation strategy
                        </Typography>
                    </Box>
                    {isStrategyPlanningComplete() && (
                        <Chip 
                            label="COMPLETED" 
                            color="success" 
                            sx={{ fontWeight: 500 }}
                            icon={<CheckIcon />}
                        />
                    )}
                </Box>
                
                <Alert severity={isStrategyPlanningComplete() ? "success" : "info"} sx={{ mb: 3 }}>
                    Define comprehensive implementation strategy for permanent corrective actions.
                    {!isStrategyPlanningComplete() && (
                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                            Required: Implementation strategy, rollout plan, and resource allocation.
                        </Typography>
                    )}
                </Alert>

            <Grid container spacing={4}>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        multiline
                        rows={6}
                        label="Implementation Strategy & Approach"
                        value={step.implementationStrategy || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ implementationStrategy: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Define overall strategy, methodology, approach for implementing permanent corrective actions..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Phased Rollout Plan"
                        value={step.rolloutPlan || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ rolloutPlan: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Detail phases, milestones, timeline, sequence of implementation..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Resource Allocation & Requirements"
                        value={step.resourceAllocation || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ resourceAllocation: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Personnel, budget, equipment, systems, facilities needed..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Stakeholder Engagement Plan"
                        value={step.stakeholderPlan || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ stakeholderPlan: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Key stakeholders, engagement strategy, communication approach..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Success Criteria & Metrics"
                        value={step.successMetrics || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ successMetrics: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Define measurable success criteria and KPIs for implementation..."
                        sx={{ 
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
            </Grid>
            </CardContent>
        </Card>
    );

    const renderPilotValidation = () => (
        <Card sx={{ 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha('#ff9800', 0.1)}, ${alpha('#ff9800', 0.05)})`,
                        border: `1px solid ${alpha('#ff9800', 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <AssessmentIcon sx={{ fontSize: 24, color: '#ff9800' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                            Pilot Testing & Validation
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Controlled pilot testing validation
                        </Typography>
                    </Box>
                    {isPilotValidationComplete() && (
                        <Chip 
                            label="COMPLETED" 
                            color="success" 
                            sx={{ fontWeight: 500 }}
                            icon={<CheckIcon />}
                        />
                    )}
                </Box>
                
                <Alert severity={isPilotValidationComplete() ? "success" : "warning"} sx={{ mb: 3 }}>
                    Conduct controlled pilot testing to validate effectiveness before full deployment.
                    {!isPilotValidationComplete() && (
                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                            Required: Pilot scope, validation criteria, and pilot results.
                        </Typography>
                    )}
                </Alert>

            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Pilot Scope & Design"
                        value={step.pilotScope || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ pilotScope: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Define pilot scope, boundaries, test environment, duration..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Validation Criteria & Methods"
                        value={step.validationCriteria || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ validationCriteria: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Success criteria, testing methods, acceptance criteria..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                
                <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'stretch' }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Pilot Start Date"
                            value={step.pilotStartDate ? dayjs(step.pilotStartDate) : null}
                            onChange={isReadOnly ? undefined : (newValue) => updateStep({ 
                                pilotStartDate: newValue ? newValue.format('YYYY-MM-DD') : null 
                            })}
                            disabled={isReadOnly}
                            format={batchTimeFormat === '24hour' ? 'MM/DD/YYYY' : 'MM/DD/YYYY'}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    sx: { 
                                        mb: 3,
                                        flex: 1,
                                        '& .MuiOutlinedInput-root': { 
                                            borderRadius: 2,
                                            '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                            '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                            '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                        }
                                    }
                                }
                            }}
                            sx={{ width: '100%', flex: 1 }}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'stretch' }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Pilot End Date"
                            value={step.pilotEndDate ? dayjs(step.pilotEndDate) : null}
                            onChange={isReadOnly ? undefined : (newValue) => updateStep({ 
                                pilotEndDate: newValue ? newValue.format('YYYY-MM-DD') : null 
                            })}
                            disabled={isReadOnly}
                            format={batchTimeFormat === '24hour' ? 'MM/DD/YYYY' : 'MM/DD/YYYY'}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    sx: { 
                                        mb: 3,
                                        flex: 1,
                                        '& .MuiOutlinedInput-root': { 
                                            borderRadius: 2,
                                            '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                            '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                            '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                        }
                                    }
                                }
                            }}
                            sx={{ width: '100%', flex: 1 }}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item xs={12} md={4}>
                    <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel>Pilot Status</InputLabel>
                        <Select
                            value={step.pilotStatus || ''}
                            onChange={isReadOnly ? undefined : (e) => updateStep({ pilotStatus: e.target.value })}
                            inputProps={{ readOnly: isReadOnly }}
                            label="Pilot Status"
                                                            sx={{ 
                                    borderRadius: 2,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.grey[400],
                                        borderWidth: 1
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.grey[600],
                                        borderWidth: 1
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.grey[700],
                                        borderWidth: 1
                                    }
                                }}
                        >
                            <MenuItem value="planned">Planned</MenuItem>
                            <MenuItem value="in_progress">In Progress</MenuItem>
                            <MenuItem value="completed">Completed</MenuItem>
                            <MenuItem value="failed">Failed</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        multiline
                        rows={6}
                        label="Pilot Results & Analysis"
                        value={step.pilotResults || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ pilotResults: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Document pilot test results, data analysis, effectiveness assessment, lessons learned..."
                        sx={{ 
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
            </Grid>
            </CardContent>
        </Card>
    );

    const renderFullDeployment = () => (
        <Card sx={{ 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha('#4caf50', 0.1)}, ${alpha('#4caf50', 0.05)})`,
                        border: `1px solid ${alpha('#4caf50', 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <EngineeringIcon sx={{ fontSize: 24, color: '#4caf50' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                            Full Scale Deployment
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Execute full-scale deployment
                        </Typography>
                    </Box>
                    {isFullDeploymentComplete() && (
                        <Chip 
                            label="COMPLETED" 
                            color="success" 
                            sx={{ fontWeight: 500 }}
                            icon={<CheckIcon />}
                        />
                    )}
                </Box>
                
                <Alert severity={isFullDeploymentComplete() ? "success" : "info"} sx={{ mb: 3 }}>
                    Execute full-scale deployment of validated corrective actions.
                    {!isFullDeploymentComplete() && (
                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                            Required: Deployment plan, rollout schedule, and completion status.
                        </Typography>
                    )}
                </Alert>

            <Grid container spacing={4}>
                <Grid item xs={12}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                            Overall Implementation Progress: {getOverallProgress()}%
                        </Typography>
                        <LinearProgress 
                            variant="determinate" 
                            value={getOverallProgress()} 
                            sx={{ height: 10, borderRadius: 5 }}
                        />
                    </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Deployment Plan & Methodology"
                        value={step.deploymentPlan || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ deploymentPlan: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Detailed deployment plan, methodology, approach for full rollout..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Rollout Schedule & Phases"
                        value={step.rolloutSchedule || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ rolloutSchedule: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Timeline, phases, milestones, dependencies for deployment..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel>Deployment Status</InputLabel>
                        <Select
                            value={step.deploymentStatus || ''}
                            onChange={isReadOnly ? undefined : (e) => updateStep({ deploymentStatus: e.target.value })}
                            inputProps={{ readOnly: isReadOnly }}
                            label="Deployment Status"
                                                            sx={{ 
                                    borderRadius: 2,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.grey[400],
                                        borderWidth: 1
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.grey[600],
                                        borderWidth: 1
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.grey[700],
                                        borderWidth: 1
                                    }
                                }}
                        >
                            <MenuItem value="planned">Planned</MenuItem>
                            <MenuItem value="in_progress">In Progress</MenuItem>
                            <MenuItem value="completed">Completed</MenuItem>
                            <MenuItem value="delayed">Delayed</MenuItem>
                            <MenuItem value="on_hold">On Hold</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Deployment Completion (%)"
                        type="number"
                        value={step.deploymentProgress || ''}
                        onChange={isReadOnly ? undefined : (e) => {
                            const value = parseFloat(e.target.value);
                            if (isNaN(value)) {
                                updateStep({ deploymentProgress: '' });
                            } else {
                                const cappedValue = Math.min(value, 100);
                                updateStep({ deploymentProgress: cappedValue });
                            }
                        }}
                        placeholder="Negative values allowed, capped at 100%"
                        InputProps={{ 
                            readOnly: isReadOnly,
                            inputProps: { step: 0.1 } 
                        }}
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                
                <Grid item xs={12}>
                    <Accordion sx={{
                        border: `1px solid ${theme.palette.grey[400]}`,
                        borderRadius: 2,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.1)}`,
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': { margin: 0 }
                    }}>
                        <AccordionSummary 
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                borderRadius: 2,
                                '&.Mui-expanded': { minHeight: 48 },
                                '& .MuiAccordionSummary-content': { margin: '12px 0' }
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                Implementation Actions Tracking ({(step.implementationActions || []).length} actions)
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {(step.implementationActions || []).map((action, index) => (
                                <Paper key={action.id} sx={{ 
                                    p: 3, 
                                    mb: 3, 
                                    border: `1px solid ${theme.palette.grey[400]}`, 
                                    borderRadius: 2, 
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.1)}`
                                }}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={8}>
                                            <TextField
                                                fullWidth
                                                label="Action Title"
                                                value={action.title}
                                                onChange={isReadOnly ? undefined : (e) => updateImplementationAction(index, 'title', e.target.value)}
                                                InputProps={{ readOnly: isReadOnly }}
                                                placeholder="Brief title for implementation action"
                                                sx={{ 
                                                    mb: 2,
                                                    '& .MuiOutlinedInput-root': { 
                                                        borderRadius: 2,
                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <FormControl fullWidth sx={{ mb: 2 }}>
                                                <InputLabel>Implementation Phase</InputLabel>
                                                <Select
                                                    value={action.phase || ''}
                                                    onChange={isReadOnly ? undefined : (e) => updateImplementationAction(index, 'phase', e.target.value)}
                                                    inputProps={{ readOnly: isReadOnly }}
                                                    label="Implementation Phase"
                                                                                    sx={{ 
                                    borderRadius: 2,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.grey[400],
                                        borderWidth: 1
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.grey[600],
                                        borderWidth: 1
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.grey[700],
                                        borderWidth: 1
                                    }
                                }}
                                                >
                                                    <MenuItem value="strategy">Strategy & Planning</MenuItem>
                                                    <MenuItem value="pilot">Pilot Testing</MenuItem>
                                                    <MenuItem value="deployment">Full Deployment</MenuItem>
                                                    <MenuItem value="training">Training</MenuItem>
                                                    <MenuItem value="monitoring">Monitoring</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Action Description"
                                                value={action.description}
                                                onChange={isReadOnly ? undefined : (e) => updateImplementationAction(index, 'description', e.target.value)}
                                                InputProps={{ readOnly: isReadOnly }}
                                                placeholder="Detailed description of implementation action..."
                                                sx={{ 
                                                    mb: 2,
                                                    '& .MuiOutlinedInput-root': { 
                                                        borderRadius: 2,
                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12} md={3}>
                                            <TextField
                                                fullWidth
                                                label="Action Owner"
                                                value={action.owner}
                                                onChange={isReadOnly ? undefined : (e) => updateImplementationAction(index, 'owner', e.target.value)}
                                                InputProps={{ readOnly: isReadOnly }}
                                                placeholder="Person responsible"
                                                sx={{ 
                                                    mb: 2,
                                                    '& .MuiOutlinedInput-root': { 
                                                        borderRadius: 2,
                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <TextField
                                                fullWidth
                                                label="Department"
                                                value={action.department}
                                                onChange={isReadOnly ? undefined : (e) => updateImplementationAction(index, 'department', e.target.value)}
                                                InputProps={{ readOnly: isReadOnly }}
                                                placeholder="Department name"
                                                sx={{ 
                                                    mb: 2,
                                                    '& .MuiOutlinedInput-root': { 
                                                        borderRadius: 2,
                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <FormControl fullWidth sx={{ mb: 2 }}>
                                                <InputLabel>Status</InputLabel>
                                                <Select
                                                    value={action.status}
                                                    onChange={isReadOnly ? undefined : (e) => updateImplementationAction(index, 'status', e.target.value)}
                                                    inputProps={{ readOnly: isReadOnly }}
                                                    label="Status"
                                                                                    sx={{ 
                                    borderRadius: 2,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.grey[400],
                                        borderWidth: 1
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.grey[600],
                                        borderWidth: 1
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.grey[700],
                                        borderWidth: 1
                                    }
                                }}
                                                >
                                                    <MenuItem value="planned">Planned</MenuItem>
                                                    <MenuItem value="in_progress">In Progress</MenuItem>
                                                    <MenuItem value="completed">Completed</MenuItem>
                                                    <MenuItem value="delayed">Delayed</MenuItem>
                                                    <MenuItem value="on_hold">On Hold</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <TextField
                                                fullWidth
                                                label="Progress (%)"
                                                type="number"
                                                value={action.progress || ''}
                                                onChange={isReadOnly ? undefined : (e) => {
                                                    const value = parseFloat(e.target.value);
                                                    if (isNaN(value)) {
                                                        updateImplementationAction(index, 'progress', '');
                                                    } else {
                                                        const cappedValue = Math.min(value, 100);
                                                        updateImplementationAction(index, 'progress', cappedValue);
                                                    }
                                                }}
                                                placeholder="Negative values allowed, capped at 100%"
                                                InputProps={{ 
                                                    readOnly: isReadOnly,
                                                    inputProps: { step: 0.1 } 
                                                }}
                                                sx={{ 
                                                    mb: 2,
                                                    '& .MuiOutlinedInput-root': { 
                                                        borderRadius: 2,
                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12}>
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    startIcon={<TrashIcon />}
                                                    onClick={isReadOnly ? undefined : () => deleteImplementationAction(index)}
                                                    disabled={isReadOnly}
                                                    sx={{ 
                                                        borderRadius: 2, 
                                                        px: 3,
                                                        opacity: isReadOnly ? 0.6 : 1
                                                    }}
                                                >
                                                    Delete Action
                                                </Button>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            ))}
                            
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={isReadOnly ? undefined : addImplementationAction}
                                disabled={isReadOnly}
                                sx={{ 
                                    mt: 2,
                                    borderRadius: 2,
                                    fontWeight: 500,
                                    px: 3,
                                    py: 1,
                                    opacity: isReadOnly ? 0.6 : 1,
                                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }
                                }}
                            >
                                Add Implementation Action
                            </Button>
                        </AccordionDetails>
                    </Accordion>
                </Grid>
            </Grid>
            </CardContent>
        </Card>
    );

    const renderTrainingChangeManagement = () => (
        <Card sx={{ 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha('#9c27b0', 0.1)}, ${alpha('#9c27b0', 0.05)})`,
                        border: `1px solid ${alpha('#9c27b0', 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <TrainingIcon sx={{ fontSize: 24, color: '#9c27b0' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                            Training & Change Management
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Ensure personnel training and change management
                        </Typography>
                    </Box>
                    {isTrainingChangeManagementComplete() && (
                        <Chip 
                            label="COMPLETED" 
                            color="success" 
                            sx={{ fontWeight: 500 }}
                            icon={<CheckIcon />}
                        />
                    )}
                </Box>
            
            <Alert severity={isTrainingChangeManagementComplete() ? "success" : "info"} sx={{ mb: 3 }}>
                Ensure personnel are trained and organizational changes are managed effectively.
                {!isTrainingChangeManagementComplete() && (
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                        Required: Training programs, change management plan, and communication plan.
                    </Typography>
                )}
            </Alert>

            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Change Management Strategy"
                        value={step.changeManagementPlan || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ changeManagementPlan: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Strategy for managing organizational change, resistance, adoption..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Communication Plan"
                        value={step.communicationPlan || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ communicationPlan: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Communication strategy, channels, messaging, frequency..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                
                <Grid item xs={12}>
                    <Accordion sx={{
                        border: `1px solid ${theme.palette.grey[400]}`,
                        borderRadius: 2,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.1)}`,
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': { margin: 0 }
                    }}>
                        <AccordionSummary 
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                borderRadius: 2,
                                '&.Mui-expanded': { minHeight: 48 },
                                '& .MuiAccordionSummary-content': { margin: '12px 0' }
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                Training Programs ({(step.trainingPrograms || []).length} programs)
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {(step.trainingPrograms || []).map((program, index) => (
                                <Paper key={program.id} sx={{ 
                                    p: 3, 
                                    mb: 3, 
                                    border: `1px solid ${theme.palette.grey[400]}`, 
                                    borderRadius: 2, 
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.1)}`
                                }}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Training Program Name"
                                                value={program.programName}
                                                onChange={isReadOnly ? undefined : (e) => updateTrainingProgram(index, 'programName', e.target.value)}
                                                InputProps={{ readOnly: isReadOnly }}
                                                placeholder="Name of training program"
                                                sx={{ 
                                                    mb: 2,
                                                    '& .MuiOutlinedInput-root': { 
                                                        borderRadius: 2,
                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Target Audience"
                                                value={program.targetAudience}
                                                onChange={isReadOnly ? undefined : (e) => updateTrainingProgram(index, 'targetAudience', e.target.value)}
                                                InputProps={{ readOnly: isReadOnly }}
                                                placeholder="Who needs this training"
                                                sx={{ 
                                                    mb: 2,
                                                    '& .MuiOutlinedInput-root': { 
                                                        borderRadius: 2,
                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12} md={4}>
                                            <FormControl fullWidth sx={{ mb: 2 }}>
                                                <InputLabel>Training Method</InputLabel>
                                                <Select
                                                    value={program.trainingMethod}
                                                    onChange={isReadOnly ? undefined : (e) => updateTrainingProgram(index, 'trainingMethod', e.target.value)}
                                                    inputProps={{ readOnly: isReadOnly }}
                                                    label="Training Method"
                                                                                    sx={{ 
                                    borderRadius: 2,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.grey[400],
                                        borderWidth: 1
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.grey[600],
                                        borderWidth: 1
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.grey[700],
                                        borderWidth: 1
                                    }
                                }}
                                                >
                                                    <MenuItem value="classroom">Classroom Training</MenuItem>
                                                    <MenuItem value="online">Online/E-Learning</MenuItem>
                                                    <MenuItem value="hands_on">Hands-on Training</MenuItem>
                                                    <MenuItem value="simulation">Simulation</MenuItem>
                                                    <MenuItem value="mentoring">Mentoring/Coaching</MenuItem>
                                                    <MenuItem value="workshop">Workshop</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                label="Duration"
                                                value={program.duration}
                                                onChange={isReadOnly ? undefined : (e) => updateTrainingProgram(index, 'duration', e.target.value)}
                                                InputProps={{ readOnly: isReadOnly }}
                                                placeholder="e.g., 4 hours, 2 days"
                                                sx={{ 
                                                    mb: 2,
                                                    '& .MuiOutlinedInput-root': { 
                                                        borderRadius: 2,
                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <FormControl fullWidth sx={{ mb: 2 }}>
                                                <InputLabel>Status</InputLabel>
                                                <Select
                                                    value={program.status}
                                                    onChange={isReadOnly ? undefined : (e) => updateTrainingProgram(index, 'status', e.target.value)}
                                                    inputProps={{ readOnly: isReadOnly }}
                                                    label="Status"
                                                                                    sx={{ 
                                    borderRadius: 2,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.grey[400],
                                        borderWidth: 1
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.grey[600],
                                        borderWidth: 1
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.grey[700],
                                        borderWidth: 1
                                    }
                                }}
                                                >
                                                    <MenuItem value="planned">Planned</MenuItem>
                                                    <MenuItem value="in_progress">In Progress</MenuItem>
                                                    <MenuItem value="completed">Completed</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Competency Requirements"
                                                value={program.competencyRequirements}
                                                onChange={isReadOnly ? undefined : (e) => updateTrainingProgram(index, 'competencyRequirements', e.target.value)}
                                                InputProps={{ readOnly: isReadOnly }}
                                                placeholder="Skills and knowledge requirements..."
                                                sx={{ 
                                                    mb: 2,
                                                    '& .MuiOutlinedInput-root': { 
                                                        borderRadius: 2,
                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Assessment Method"
                                                value={program.assessmentMethod}
                                                onChange={isReadOnly ? undefined : (e) => updateTrainingProgram(index, 'assessmentMethod', e.target.value)}
                                                InputProps={{ readOnly: isReadOnly }}
                                                placeholder="How competency will be assessed..."
                                                sx={{ 
                                                    mb: 2,
                                                    '& .MuiOutlinedInput-root': { 
                                                        borderRadius: 2,
                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Completion Criteria"
                                                value={program.completionCriteria}
                                                onChange={isReadOnly ? undefined : (e) => updateTrainingProgram(index, 'completionCriteria', e.target.value)}
                                                InputProps={{ readOnly: isReadOnly }}
                                                placeholder="Criteria for successful completion..."
                                                sx={{ 
                                                    mb: 2,
                                                    '& .MuiOutlinedInput-root': { 
                                                        borderRadius: 2,
                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12}>
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    startIcon={<TrashIcon />}
                                                    onClick={isReadOnly ? undefined : () => {
                                                        const updatedPrograms = (step.trainingPrograms || []).filter((_, i) => i !== index);
                                                        updateStep({ trainingPrograms: updatedPrograms });
                                                    }}
                                                    disabled={isReadOnly}
                                                    sx={{ 
                                                        borderRadius: 2, 
                                                        px: 3,
                                                        opacity: isReadOnly ? 0.6 : 1
                                                    }}
                                                >
                                                    Delete Program
                                                </Button>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            ))}
                            
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={isReadOnly ? undefined : addTrainingProgram}
                                disabled={isReadOnly}
                                sx={{ 
                                    mt: 2,
                                    borderRadius: 2,
                                    fontWeight: 500,
                                    px: 3,
                                    py: 1,
                                    opacity: isReadOnly ? 0.6 : 1,
                                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }
                                }}
                            >
                                Add Training Program
                            </Button>
                        </AccordionDetails>
                    </Accordion>
                </Grid>
            </Grid>
            </CardContent>
        </Card>
    );

    const renderMonitoringControl = () => (
        <Card sx={{ 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha('#f44336', 0.1)}, ${alpha('#f44336', 0.05)})`,
                        border: `1px solid ${alpha('#f44336', 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <TimelineIcon sx={{ fontSize: 24, color: '#f44336' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                            Ongoing Monitoring & Control
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Establish monitoring and control mechanisms
                        </Typography>
                    </Box>
                    {isMonitoringControlComplete() && (
                        <Chip 
                            label="COMPLETED" 
                            color="success" 
                            sx={{ fontWeight: 500 }}
                            icon={<CheckIcon />}
                        />
                    )}
                </Box>
            
            <Alert severity={isMonitoringControlComplete() ? "success" : "warning"} sx={{ mb: 3 }}>
                Establish ongoing monitoring and control mechanisms to ensure sustained effectiveness.
                {!isMonitoringControlComplete() && (
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                        Required: Monitoring plan, KPI definitions, and control mechanisms.
                    </Typography>
                )}
            </Alert>

            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Monitoring Plan & Schedule"
                        value={step.monitoringPlan || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ monitoringPlan: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Define ongoing monitoring approach, frequency, responsibilities..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="KPI Definitions & Targets"
                        value={step.kpiDefinitions || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ kpiDefinitions: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Key performance indicators, metrics, targets, thresholds..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Control Mechanisms"
                        value={step.controlMechanisms || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ controlMechanisms: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Process controls, checkpoints, escalation procedures..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Reporting & Dashboards"
                        value={step.reportingPlan || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ reportingPlan: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Reporting structure, dashboards, data visualization..."
                        sx={{ 
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
            </Grid>
            </CardContent>
        </Card>
    );

    const renderComplianceSustainability = () => (
        <Card sx={{ 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha('#607d8b', 0.1)}, ${alpha('#607d8b', 0.05)})`,
                        border: `1px solid ${alpha('#607d8b', 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <ComplianceIcon sx={{ fontSize: 24, color: '#607d8b' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                            Compliance Validation & Sustainability
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Ensure compliance and long-term sustainability
                        </Typography>
                    </Box>
                    {isComplianceSustainabilityComplete() && (
                        <Chip 
                            label="COMPLETED" 
                            color="success" 
                            sx={{ fontWeight: 500 }}
                            icon={<CheckIcon />}
                        />
                    )}
                </Box>
            
            <Alert severity={isComplianceSustainabilityComplete() ? "success" : "info"} sx={{ mb: 3 }}>
                Ensure regulatory compliance and long-term sustainability of implemented changes.
                {!isComplianceSustainabilityComplete() && (
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                        Required: Compliance validation, sustainability plan, and documentation completion.
                    </Typography>
                )}
            </Alert>

            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Compliance Validation"
                        value={step.complianceValidation || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ complianceValidation: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Regulatory compliance validation, audit readiness, documentation..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Long-term Sustainability Plan"
                        value={step.sustainabilityPlan || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ sustainabilityPlan: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Long-term sustainability, maintenance, continuous improvement..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel>Documentation Status</InputLabel>
                        <Select
                            value={step.documentationStatus || ''}
                            onChange={isReadOnly ? undefined : (e) => updateStep({ documentationStatus: e.target.value })}
                            inputProps={{ readOnly: isReadOnly }}
                            label="Documentation Status"
                                                            sx={{ 
                                    borderRadius: 2,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.grey[400],
                                        borderWidth: 1
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.grey[600],
                                        borderWidth: 1
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.grey[700],
                                        borderWidth: 1
                                    }
                                }}
                        >
                            <MenuItem value="in_progress">In Progress</MenuItem>
                            <MenuItem value="completed">Completed</MenuItem>
                            <MenuItem value="approved">Approved</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Implementation Effectiveness (%)"
                        type="number"
                        value={step.effectivenessRating || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ effectivenessRating: e.target.value })}
                        placeholder="0-100"
                        InputProps={{ 
                            readOnly: isReadOnly,
                            inputProps: { max: 100 } 
                        }}
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Final Implementation Summary"
                        value={step.implementationSummary || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ implementationSummary: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Comprehensive summary of implementation, outcomes, lessons learned..."
                        sx={{ 
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
            </Grid>
            </CardContent>
        </Card>
    );

    const renderPhase = () => {
        switch (activePhase) {
            case 0: return renderStrategyPlanning();
            case 1: return renderPilotValidation();
            case 2: return renderFullDeployment();
            case 3: return renderTrainingChangeManagement();
            case 4: return renderMonitoringControl();
            case 5: return renderComplianceSustainability();
            default: return null;
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ 
                p: 4, 
                backgroundColor: alpha(theme.palette.warning.main, 0.1), 
                borderRadius: 4, 
                border: `1px solid ${theme.palette.warning.main}`, 
                boxShadow: `0 8px 32px ${alpha(theme.palette.warning.main, 0.12)}`,
                backdropFilter: 'blur(20px)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha(theme.palette.warning.main, 0.1)}, ${alpha(theme.palette.warning.main, 0.05)})`,
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <EngineeringIcon sx={{ fontSize: 24, color: theme.palette.warning.main }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ 
                            fontWeight: 500, 
                            color: theme.palette.warning.dark, 
                            mb: 0.5
                        }}>
                            D6: Implementation of Permanent Actions
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Systematically implement permanent corrective actions following industry best practices for change management and validation.
                        </Typography>
                    </Box>
                </Box>
                
                <Stepper 
                    activeStep={-1}
                    sx={{ 
                        mb: 4,
                        '& .MuiStep-root': {
                            opacity: 1,
                            transition: 'opacity 0.3s ease'
                        },
                        '& .MuiStepLabel-root': {
                            cursor: 'pointer',
                            opacity: 0.4,
                            '&[data-active="true"]': {
                                opacity: 1
                            },
                            transition: 'opacity 0.3s ease'
                        },
                        '& .MuiStepIcon-root': {
                            fontSize: '1.5rem',
                            color: theme.palette.grey[400],
                            '&.Mui-active': {
                                color: theme.palette.primary.main,
                            },
                            '&.Mui-completed': {
                                color: theme.palette.grey[400],
                            }
                        },
                        '& .MuiStepIcon-text': {
                            fill: 'white',
                            fontWeight: 500
                        }
                    }} 
                    alternativeLabel
                >
                    {implementationPhases.map((phase, index) => (
                        <Step key={phase.name}>
                            <StepLabel 
                                onClick={() => setActivePhase(index)}
                                data-active={activePhase === index}
                                StepIconProps={{
                                    icon: index + 1
                                }}
                                sx={{
                                    '& .MuiStepIcon-root': {
                                        color: activePhase === index ? theme.palette.primary.main : theme.palette.grey[400],
                                        '&.Mui-completed': {
                                            color: theme.palette.grey[400],
                                        }
                                    }
                                }}
                            >
                                {phase.name}
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {renderPhase()}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                    <Button 
                        disabled={activePhase === 0}
                        onClick={() => setActivePhase(prev => prev - 1)}
                        variant="outlined"
                        sx={{ 
                            borderRadius: 2, 
                            px: 3,
                            fontWeight: 500,
                            textTransform: 'none',
                            borderColor: theme.palette.grey[400],
                            color: theme.palette.text.primary,
                            '&:hover': {
                                borderColor: theme.palette.grey[600],
                                backgroundColor: alpha(theme.palette.grey[600], 0.04)
                            },
                            '&:disabled': {
                                borderColor: theme.palette.grey[300],
                                color: theme.palette.grey[400]
                            }
                        }}
                    >
                        Previous Phase
                    </Button>
                    <Button 
                        disabled={activePhase === implementationPhases.length - 1}
                        onClick={() => setActivePhase(prev => prev + 1)}
                        variant="contained"
                        sx={{ 
                            borderRadius: 2, 
                            px: 3,
                            fontWeight: 500,
                            textTransform: 'none',
                            backgroundColor: theme.palette.primary.main,
                            color: 'white',
                            '&:hover': {
                                backgroundColor: theme.palette.primary.dark
                            },
                            '&:disabled': {
                                backgroundColor: theme.palette.grey[300],
                                color: theme.palette.grey[500]
                            }
                        }}
                    >
                        Next Phase
                    </Button>
                </Box>
                
                <Paper sx={{ 
                    p: 3, 
                    mt: 4, 
                    backgroundColor: '#fff', 
                    border: `1px solid ${theme.palette.grey[400]}`,
                    borderRadius: 2,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.1)}`
                }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={step.completed || false}
                                onChange={isReadOnly ? undefined : (e) => {
                                    if (!isFormValid() && e.target.checked) {
                                        alert('Please complete all implementation phases before marking as complete.');
                                        return;
                                    }
                                    updateStep({ completed: e.target.checked });
                                }}
                                disabled={isReadOnly || !isFormValid()}
                                sx={{ '& .MuiSvgIcon-root': { fontSize: 28, color: isFormValid() ? '#2196f3' : '#ccc' } }}
                            />
                        }
                        label=""
                    />
                    <Typography variant="h6" sx={{ fontWeight: 500, color: isFormValid() ? '#0277bd' : '#666', display: 'inline' }}>
                        D6 Implementation Complete - Permanent Actions Successfully Deployed
                    </Typography>
                    {!isFormValid() && (
                        <Typography variant="body2" sx={{ color: '#f44336', fontStyle: 'italic', display: 'block', mt: 1 }}>
                            (Complete all phases: Strategy, Pilot, Deployment, Training, Monitoring, and Compliance)
                        </Typography>
                    )}
                </Paper>
            </Box>
        </LocalizationProvider>
    );
} 